import { ShieldCheck, HeartHandshake, Users, Sparkles, Scale, Sprout } from "lucide-react";

const VALUES = [
    { icon: HeartHandshake, name: "Dignity", body: "Every person is met with respect and consideration." },
    { icon: ShieldCheck, name: "Integrity", body: "Honest, transparent and accountable in every action." },
    { icon: Users, name: "Inclusion", body: "Communities of every ability, age and background belong." },
    { icon: Scale, name: "Accountability", body: "Verified data, responsible stewardship, honest reporting." },
    { icon: Sparkles, name: "Partnership", body: "Shared purpose with communities, partners and volunteers." },
    { icon: Sprout, name: "Sustainability", body: "Programmes designed for lasting community ownership." },
];

const STEPS = [
    "Listen to community needs.",
    "Verify and responsibly record requests.",
    "Identify practical interventions.",
    "Mobilise partners, volunteers and resources.",
    "Implement with community participation.",
    "Measure and communicate impact.",
    "Strengthen sustainability and local ownership.",
];

export default function About() {
    return (
        <div>
            <PageHero title="About Global Seed Foundation" subtitle="Community empowerment · Warren Park Connect" />

            <section className="gsf-section" data-testid="about-who">
                <div className="gsf-container grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="gsf-eyebrow">Who We Are</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            Rooted in community. Guided by dignity.
                        </h2>
                    </div>
                    <div className="md:col-span-7 space-y-5 text-[color:var(--gsf-body)] leading-relaxed">
                        <p>
                            Global Seed Foundation is a community-centred organisation established to support practical
                            development, dignity and sustainable empowerment. The Foundation works with communities to
                            identify needs, mobilise support, build partnerships and create initiatives that produce
                            measurable improvements in people’s lives.
                        </p>
                        <p data-testid="about-humanitarian-paragraph">
                            In addition to supporting sustainable livelihoods and community enterprise development,
                            Global Seed Foundation responds to humanitarian needs affecting vulnerable households. The
                            Foundation provides compassionate assistance during times of crisis, including support to
                            bereaved families requiring assistance to give their loved ones dignified burials, while
                            continuing to promote long-term resilience through community-led development.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="about-vision-mission">
                <div className="gsf-container grid md:grid-cols-2 gap-8">
                    <div className="gsf-card p-10">
                        <div className="gsf-eyebrow">Our Vision</div>
                        <p className="mt-3 font-serif text-2xl text-[color:var(--gsf-green-700)] leading-snug">
                            Inclusive, resilient and empowered communities in which every person has an opportunity to
                            live with dignity and contribute meaningfully to society.
                        </p>
                    </div>
                    <div className="gsf-card p-10">
                        <div className="gsf-eyebrow">Our Mission</div>
                        <p className="mt-3 font-serif text-2xl text-[color:var(--gsf-green-700)] leading-snug">
                            To support vulnerable people and strengthen communities through humanitarian assistance,
                            livelihood development, inclusive empowerment, skills, partnerships and practical community
                            initiatives.
                        </p>
                    </div>
                </div>
            </section>

            <section className="gsf-section" data-testid="about-values">
                <div className="gsf-container">
                    <div className="max-w-2xl">
                        <div className="gsf-eyebrow">Our Values</div>
                        <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">
                            The principles that guide every decision.
                        </h2>
                    </div>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {VALUES.map((v, i) => (
                            <div key={v.name} className="gsf-card p-7" data-testid={`value-${i}`}>
                                <div className="w-12 h-12 rounded-xl bg-[color:var(--gsf-soft-green)] flex items-center justify-center text-[color:var(--gsf-green-700)]">
                                    <v.icon size={22} strokeWidth={1.7} />
                                </div>
                                <h3 className="mt-5 font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">{v.name}</h3>
                                <p className="mt-2 text-sm text-[color:var(--gsf-body)] leading-relaxed">{v.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[color:var(--gsf-green-900)] text-white gsf-section" data-testid="about-how">
                <div className="gsf-container grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">How We Work</div>
                        <h2 className="mt-3 font-serif text-3xl font-bold">A responsible, community-led operating model.</h2>
                    </div>
                    <div className="md:col-span-7">
                        <ol className="space-y-4">
                            {STEPS.map((s, i) => (
                                <li key={s} className="flex items-start gap-4" data-testid={`step-${i}`}>
                                    <span className="mt-0.5 w-9 h-9 rounded-full bg-[color:var(--gsf-gold-500)] text-[#1f2937] font-bold flex items-center justify-center shrink-0">
                                        {i + 1}
                                    </span>
                                    <span className="text-white/85 leading-relaxed">{s}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            <section className="gsf-section" data-testid="about-governance">
                <div className="gsf-container">
                    <div className="gsf-eyebrow">Governance</div>
                    <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">
                        Governance and accountability
                    </h2>
                    <div className="mt-6 gsf-card p-8 max-w-3xl">
                        <p className="text-[color:var(--gsf-body)] leading-relaxed">
                            Governance details, trustee information, registration status and organisational documents
                            will be published on this page once formally confirmed. Global Seed Foundation is committed
                            to transparent governance, verified reporting and responsible stewardship of all resources
                            entrusted to the Foundation.
                        </p>
                        <p className="mt-4 text-sm text-gray-500">
                            For governance enquiries, please contact{" "}
                            <a className="underline text-[color:var(--gsf-green-700)]" href="mailto:impact@globalseedzim.org">impact@globalseedzim.org</a>.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}

export function PageHero({ title, subtitle }) {
    return (
        <section className="bg-[color:var(--gsf-green-700)] text-white pt-16 pb-20 sm:pt-24 sm:pb-28" data-testid="page-hero">
            <div className="gsf-container">
                <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">{subtitle}</div>
                <h1 className="mt-3 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{title}</h1>
            </div>
        </section>
    );
}
